import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React from 'react'

interface VisibilityConfirmationDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (makeVisible: boolean) => void
  selectedAccruals: any[]
  action: 'visible' | 'hidden'
  periodLabels: Record<string, string>
}

const VisibilityConfirmationDialog: React.FC<VisibilityConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  selectedAccruals,
  action,
  periodLabels,
}) => {
  const formatCurrency = (amount: number | null) => {
    if (amount === null) return '-'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Confirm Make {action === 'visible' ? 'Visible' : 'Hidden'}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          You are about to make {selectedAccruals.length} accrual{selectedAccruals.length !== 1 ? 's' : ''} {action}.
          Please review the items below and confirm your action.
        </Typography>

        <TableContainer sx={{ mt: 3, maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Employee</strong>
                </TableCell>
                <TableCell>
                  <strong>Team</strong>
                </TableCell>
                <TableCell>
                  <strong>Period</strong>
                </TableCell>
                <TableCell>
                  <strong>Line Item</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Balance</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedAccruals.map((accrual) => (
                <TableRow key={accrual.id}>
                  <TableCell>{`${accrual.userFirstName} ${accrual.userLastName}`.trim()}</TableCell>
                  <TableCell>{accrual.organizationName || '-'}</TableCell>
                  <TableCell>{periodLabels[accrual.periodId] || accrual.periodId}</TableCell>
                  <TableCell>
                    {accrual.lineItemCount
                      ? `(${accrual.lineItemCount}) Line Items`
                      : `${accrual.allocationTableName} | ${accrual.allocationRowName}`}
                  </TableCell>
                  <TableCell>{accrual.status.charAt(0).toUpperCase() + accrual.status.slice(1)}</TableCell>
                  <TableCell align="right">{formatCurrency(accrual.proratedPayoutAmount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(action === 'visible')}
          variant="contained"
          color={action === 'visible' ? 'primary' : 'warning'}
        >
          Make {action === 'visible' ? 'Visible' : 'Hidden'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default VisibilityConfirmationDialog
